import * as get from 'lodash/get';
import * as has from 'lodash/has';

import { SecurityBuilder } from './../builders/security.builder';
import { Security } from './../models/security';

export class SecurityDirector {
	public construct(securityJson: any): Security {
		const performanceMap = securityJson.performanceMap || {};
		const datesMap = securityJson.datesMap || {};
		const classifications = securityJson.classifications || {};

		let prospectusExpWaiverType = '-';
		if (has(securityJson, 'characteristicsMap.prospectusFeeReductionType')) {
			if (securityJson?.characteristicsMap?.prospectusFeeReductionContractualReduction?.toLowerCase() === 'true') {
				prospectusExpWaiverType = 'Contractual';
			} else {
				prospectusExpWaiverType = 'Non-Contractual';
			}
		}

		return new SecurityBuilder()
			.setCusip(securityJson.cusip)
			.setDescription(securityJson.description)
			.setIdentifier(securityJson.securityId)
			.setIdentifierType('SECURITY_ID') // should be updated to correct identifier
			.setSubType('HOLDING')
			.setTicker(securityJson.ticker)
			.setUniqueId(securityJson.securityId)
			.setWeight(securityJson.holdingPercent ? securityJson.holdingPercent : securityJson.weight)
			.setValue(securityJson.value)
			.setAsOfDate(datesMap.performance)
			.setAssetClass(securityJson.assetClass)
			.setBlackrockFund(securityJson.blackrockFund)
			.setInceptionDate(securityJson.inceptionDate)
			.setAssetClassCategory(classifications.acAssetClass)
			.setNavMonthlyPerfOneYear(performanceMap.returnY1)
			.setNavMonthlyPerfFiveYears(performanceMap.returnY5)
			.setNavMonthlyPerfTenYears(performanceMap.returnY10)
			.setNavMonthlySinceInception(performanceMap.returnSinceInception)
			.setNavMonthlyPerfYTD(performanceMap.returnYtd)
			.setPortfolioId(securityJson.securityPortfolioId ? securityJson.securityPortfolioId : securityJson.portfolioId)
			.setParentPortfolioId(securityJson.parentPortfolioId)
			.setStdNavPerfOneYear(securityJson.stdPerfOneYearAnnualized)
			.setStdNavPerfFiveYears(securityJson.stdPerfFiveYearAnnualized)
			.setStdNavPerfTenYears(securityJson.stdPerfTenYearAnnualized)
			.setStdNavPerfSinceInception(securityJson.stdPerfSinceInceptionAnnualized)
			.setStdPerfGrossRatio(securityJson.analyticsMap && securityJson.analyticsMap.prospectusGrossExpenseRatio ? securityJson.analyticsMap.prospectusGrossExpenseRatio : null)
			.setNetExpRatio(get(securityJson, 'prospectusNetExpenseRatio', null))
			.setStdSecYield(securityJson.secYield)
			.setStdMktPerfOneYear(securityJson.stdPerfMarketPriceOneYearAnnualized)
			.setStdMktPerfFiveYears(securityJson.stdPerfMarketPriceFiveYearAnnualized)
			.setStdMktPerfTenYears(securityJson.stdPerfMarketPriceTenYearAnnualized)
			.setStdMktPerfSinceInception(securityJson.stdPerfMarketPriceSinceInceptionAnnualized)
			.setStdPerfAsOfDate(securityJson.stdPerfAsOfDate)
			.setMorningstarCategory(securityJson.morningstarCategory)
			.setMorningstarFundId(securityJson.morningstarFundId)
			.setUnSubSecYield(get(securityJson, 'secYieldUnsubsidized', null))
			.setProspectusExpWaiverType(prospectusExpWaiverType)
			.setProspectusExpWaiverDate(get(securityJson, 'characteristicsMap.prospectusFeeReductionExpirationDate', null))
			.setOtherIdentifiers(securityJson.otherIdentifier)
			.setSubstitutedFrom(securityJson.substitutedFrom)
			.build();
	}

	public constructBaseHolding(securityJson: any): Security {
		const classifications = securityJson.classifications || {};

		return new SecurityBuilder()
			.setCusip(securityJson.cusip)
			.setDescription(securityJson.description)
			.setIdentifier(securityJson.securityId)
			.setIdentifierType('SECURITY_ID')
			.setSubType('HOLDING')
			.setTicker(securityJson.ticker)
			.setUniqueId(securityJson.securityId)
			.setWeight(securityJson.holdingPercent ? securityJson.holdingPercent : securityJson.weight)
			.setValue(securityJson.value)
			.setAssetClass(securityJson.assetClass)
			.setBlackrockFund(securityJson.blackrockFund)
			.setAssetClassCategory(classifications.acAssetClass)
			.setPortfolioId(securityJson.securityPortfolioId ? securityJson.securityPortfolioId : securityJson.portfolioId)
			.setParentPortfolioId(securityJson.parentPortfolioId)
			.build();
	}

	public constructByParams(
		description: string,
		identifier: string,
		identifierType: string,
		subType: string,
		ticker: string,
		morningstarSecId: string,
		globalId: string,
		aladdinId: string,
		cusip: string,
		market: string,
		currency: string,
		isin: string,
		weight: number,
		value: number,
		uniqueId?: string,
		assetClass?: string,
		assetType?: string,
		morningstarCategory?: string,
		morningstarFundId?: string,
		otherIdentifier?: any,
		substitutedFrom?: any,
		peOrder?: number,
		aladdinPortfolioCode?: string // for partner portfolio with model securities
	): Security {
		return new SecurityBuilder()
			.setDescription(description)
			.setIdentifier(identifier)
			.setIdentifierType(identifierType)
			.setSubType(subType)
			.setTicker(ticker)
			.setAladdinId(aladdinId)
			.setCusip(cusip)
			.setGlobalId(globalId)
			.setMarket(market)
			.setCurrency(currency)
			.setIsin(isin)
			.setMorningstarSecId(morningstarSecId)
			.setWeight(weight)
			.setValue(value)
			.setUniqueId(uniqueId)
			.setAssetClass(assetClass)
			.setAssetType(assetType)
			.setMorningstarCategory(morningstarCategory)
			.setMorningstarFundId(morningstarFundId)
			.setOtherIdentifiers(otherIdentifier)
			.setSubstitutedFrom(substitutedFrom)
			.setPeOrder(peOrder)
			.setAladdinPortfolioCode(aladdinPortfolioCode)
			.build();
	}
}
